/**
 * Read-only view of a user's MFA enrollment, for admins looking at someone else's profile (#14476).
 *
 * Deliberately not editable: only the user themselves can turn MFA on or off, from their own
 * profile. This shows the user-level enrollment bit as-is, so "the org requires MFA but this
 * person has not enrolled yet" stays visible rather than being rounded up to "on".
 *
 * Tri-state (#14476 finding 4): `enabled` is true/false for a known user, or null/undefined when
 * the identity provider has no such account (an orphaned mirror row). The unknown case renders an
 * indeterminate checkbox and says so, rather than asserting a definite on/off from a stale row.
 *
 * `layout="grid"` matches the label-left/field-right forms (ManageSponsor, Me);
 * the default block layout matches the stacked Add/Edit user forms.
 */
import { useEffect, useRef } from "react";

export default function MfaReadOnlyField({
  enabled,
  idSuffix = "",
  layout = "block",
}) {
  const isUnknown = enabled === null || enabled === undefined;
  const checked = !isUnknown && Boolean(enabled);
  let state = "disabled";
  if (isUnknown) {
    state = "unknown";
  } else if (checked) {
    state = "enabled";
  }
  const inputId = idSuffix ? `mfa-readonly-${idSuffix}` : "mfa-readonly";

  // `indeterminate` is a DOM property, not an attribute, so it must be set imperatively.
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = isUnknown;
  }, [isUnknown]);

  let statusText = "Not enabled. Only the user can change this.";
  if (isUnknown) {
    statusText = "Unknown. This account was not found in the identity provider.";
  } else if (checked) {
    statusText = "Enabled by the user. Only the user can change this.";
  }

  const control = (
    <div className="flex items-start opacity-70">
      <div className="flex h-6 shrink-0 items-center">
        <input
          id={inputId}
          ref={inputRef}
          data-testid="mfa-readonly-checkbox"
          type="checkbox"
          checked={checked}
          disabled
          readOnly
          aria-readonly="true"
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 disabled:cursor-not-allowed"
        />
      </div>
      <div className="ml-3 min-w-0">
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-500"
        >
          Multi-Factor Authentication
        </label>
        <p
          className="mt-1 text-xs text-gray-500"
          data-testid="mfa-readonly-status"
        >
          {statusText}
        </p>
      </div>
    </div>
  );

  if (layout === "grid") {
    return (
      <div
        data-testid="mfa-readonly-field"
        data-mfa-state={state}
        className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6"
      >
        <span className="block text-sm font-medium text-gray-900 sm:pt-1.5">
          Security
        </span>
        <div className="mt-2 sm:col-span-2 sm:mt-0">{control}</div>
      </div>
    );
  }

  return (
    <div
      data-testid="mfa-readonly-field"
      data-mfa-state={state}
      className="border-b border-gray-200 pb-6"
    >
      <h2 className="font-semibold text-lg">Security</h2>
      <div className="mt-4">{control}</div>
    </div>
  );
}
